// LiteratureSuggestForm — reusable "suggest literature for the KB" form
// (V1.0-beta). Extracted from PersistentAI's "About the AI" modal so the
// SAME form can also live in the Feedback tab. One source of truth for the
// submit logic — DOI/URL + title + note + an optional single PDF upload —
// posted as multipart/form-data to POST /kb/suggest (the kb_suggestions
// review queue; nothing auto-ingests into the curated KB).
//
// Props:
//   theme       — theme name ('dark' | 'light'); used if `colors` not given.
//   colors      — optional pre-computed themeColors(theme) object (`c`).
//   compact     — dense monospace styling for the narrow chat modal (default
//                 false → roomier styling for the Feedback tab).
//   onSubmitted — optional callback invoked with the backend response on a
//                 successful submit.
//
// A submission is valid when EITHER a DOI/URL is provided OR a PDF file is
// attached (so a user can hand us a paper with no DOI). Files are validated
// client-side (PDF only, ≤ 20 MB) before upload; the backend re-checks.

import React, { useState, useRef } from 'react';
import axios from 'axios';
import { themeColors } from '../theme';

// KB router is mounted at /kb (NOT under /api/v1) — same convention as
// services/kbService.js and PersistentAI.js.
const KB_API = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/kb`;

const MAX_FILE_BYTES = 20 * 1024 * 1024; // 20 MB — mirrors the server limit.

function isPdf(file) {
  if (!file) return false;
  const nameOk = /\.pdf$/i.test(file.name || '');
  const typeOk = file.type === 'application/pdf' || file.type === '';
  return nameOk && typeOk;
}

function humanSize(bytes) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}

export default function LiteratureSuggestForm({
  theme = 'dark',
  colors,
  compact = false,
  onSubmitted,
}) {
  const c = colors || themeColors(theme);

  const [doiOrUrl, setDoiOrUrl] = useState('');
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [file, setFile] = useState(null);
  const [phase, setPhase] = useState('idle');      // idle | submitting | done
  const [resultMsg, setResultMsg] = useState('');
  const [fileStored, setFileStored] = useState(null); // bool | null (no file)
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const fontSize = compact ? 12 : 13;
  const mono = 'var(--mono, IBM Plex Mono, monospace)';

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    padding: compact ? '6px 9px' : '8px 10px',
    borderRadius: compact ? 4 : 3,
    border: `1px solid ${c.border}`,
    background: c.input, color: c.inputText,
    fontFamily: mono, fontSize, outline: 'none',
  };

  function handleFilePick(e) {
    setError('');
    const f = e.target.files && e.target.files[0];
    if (!f) { setFile(null); return; }
    if (!isPdf(f)) {
      setFile(null);
      setError('Only PDF files are accepted.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    if (f.size > MAX_FILE_BYTES) {
      setFile(null);
      setError(`File is too large (${humanSize(f.size)}). Max 20 MB.`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setFile(f);
  }

  function clearFile() {
    setFile(null);
    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const canSubmit = (!!doiOrUrl.trim() || !!file) && phase !== 'submitting';

  async function handleSubmit() {
    if (!canSubmit) return;
    setPhase('submitting');
    setError('');
    try {
      const fd = new FormData();
      if (doiOrUrl.trim()) fd.append('doi_or_url', doiOrUrl.trim());
      if (title.trim())    fd.append('title', title.trim());
      if (note.trim())     fd.append('note', note.trim());
      if (file)            fd.append('file', file, file.name);
      // Do NOT set Content-Type manually — the browser adds the multipart
      // boundary. The global axios interceptor attaches the auth token.
      const { data } = await axios.post(`${KB_API}/suggest`, fd,
        { timeout: 30000 });
      setResultMsg(data?.message || 'Thanks — suggestion submitted for review.');
      setFileStored(file ? !!data?.file_stored : null);
      setPhase('done');
      if (typeof onSubmitted === 'function') onSubmitted(data);
    } catch (err) {
      const status = err?.response?.status;
      let msg;
      if (status === 401) {
        msg = 'Please sign in to suggest literature.';
      } else if (status === 413) {
        msg = 'File is too large — max 20 MB.';
      } else if (status === 415) {
        msg = 'Only PDF files are accepted.';
      } else {
        const detail = err?.response?.data?.detail;
        msg = typeof detail === 'string' ? detail
          : Array.isArray(detail)
            ? detail.map(d => d.msg).join('; ')
            : 'Could not submit — try again.';
      }
      setError(msg);
      setPhase('idle');
    }
  }

  const bodyText = {
    fontSize, color: c.textMuted, lineHeight: 1.5, margin: 0,
    fontFamily: mono,
  };

  if (phase === 'done') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <p style={{ ...bodyText, color: c.text }}>
          {'✓'} {resultMsg}
        </p>
        {fileStored === false && (
          <p style={{ ...bodyText, color: c.textMuted, fontStyle: 'italic' }}>
            (Your text was saved, but the attached PDF could not be stored yet —
            file storage is pending setup. Nothing was lost; it is queued.)
          </p>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <input
        type="text"
        placeholder="DOI or URL (required unless you attach a PDF)"
        value={doiOrUrl}
        onChange={e => setDoiOrUrl(e.target.value)}
        style={inputStyle}
        aria-label="DOI or URL"
      />
      <input
        type="text"
        placeholder="Title (optional)"
        value={title}
        onChange={e => setTitle(e.target.value)}
        style={inputStyle}
        aria-label="Title"
      />
      <textarea
        placeholder="Why it matters (optional)"
        value={note}
        onChange={e => setNote(e.target.value)}
        rows={2}
        style={{ ...inputStyle, resize: 'none' }}
        aria-label="Why it matters"
      />

      {/* Hidden native input + a styled trigger, plus the selected filename. */}
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf,.pdf"
        onChange={handleFilePick}
        style={{ display: 'none' }}
        aria-label="Attach a PDF paper"
      />
      {file ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: compact ? '5px 9px' : '7px 10px',
          border: `1px solid ${c.border}`, borderRadius: compact ? 4 : 3,
          background: c.bgSecondary, fontSize: fontSize - 1,
          color: c.text, fontFamily: mono,
        }}>
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
                         whiteSpace: 'nowrap' }}>
            {'\u{1F4CE}'} {file.name} <span style={{ color: c.textMuted }}>
              ({humanSize(file.size)})
            </span>
          </span>
          <button
            type="button"
            onClick={clearFile}
            title="Remove attached file"
            aria-label="Remove attached file"
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: c.textMuted, fontSize: fontSize + 1, lineHeight: 1,
              padding: '0 2px',
            }}
          >
            {'✕'}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          style={{
            alignSelf: 'flex-start',
            background: 'transparent', color: c.textMuted,
            border: `1px dashed ${c.border}`, borderRadius: compact ? 4 : 3,
            padding: compact ? '5px 10px' : '7px 12px',
            fontFamily: mono, fontSize: fontSize - 1, cursor: 'pointer',
          }}
        >
          {'\u{1F4CE}'} Attach a PDF (optional, max 20 MB)
        </button>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          style={{
            background: !canSubmit ? c.btnInactive : c.btnPrimary,
            color: !canSubmit ? c.btnInactiveText : c.btnPrimaryText,
            border: 'none', borderRadius: 4,
            padding: compact ? '6px 14px' : '8px 18px',
            fontFamily: mono, fontSize: compact ? 11 : 12, fontWeight: 600,
            cursor: !canSubmit ? 'default' : 'pointer',
          }}
        >
          {phase === 'submitting' ? 'Submitting…' : 'Submit suggestion'}
        </button>
        {error && (
          <span style={{ color: c.red, fontSize: fontSize - 1 }}>{error}</span>
        )}
      </div>
    </div>
  );
}
